import { useDebounce } from '@neo/hooks/use-debounce'
import { TimeUnits } from '@shared/types'
import { convertToMs } from '@shared/utils'
import { useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query'
import axios, { type AxiosError } from 'axios'
import { useLocation, useParams } from 'router-v6'
import { v4 } from 'uuid'
import { queryClient } from 'v2/configs/rq-setup'
import type {
  GenerateCodeParams,
  GenerateFunctionSchemaParams,
  GenerateHttpFunctionSchemaParams,
  RMapActionParametersPayload,
} from 'v2/pages/flow/sidebar/types'
import { useAppNavigate } from 'v2/shared/hooks/use-app-navigate'
import { useFeatureFlags } from 'v2/shared/hooks/use-feature-flags'
import { executionService } from 'v2/shared/services/execution-service'
import { externalService } from 'v2/shared/services/external-apps-service'
import { flowService } from 'v2/shared/services/flow-service'
import { playgroundService } from 'v2/shared/services/playground-service'
import { settingsService } from 'v2/shared/services/settings-service'
import { getErrorText } from 'v2/shared/utils/api-error-text'
import { isEmbedMode, isSubAccountEmbedSession } from 'v2/shared/utils/embed-session'
import { flowQueryKeys } from 'v2/shared/utils/query-key-management'
import { FlowSourceTypes, FlowTypes } from 'v2/types'
import { ConvChannels } from 'v2/types/api/conversations'
import { getLocalAuth } from 'v2/utils'
import { AnalyticsEvents, track } from 'v2/utils/analytics'
import dayjs from 'v2/utils/dayjs'
import { agentLogger, phoneLogger } from 'v2/utils/logger'
import { phoneSDK } from 'v2/utils/phone-sdk'
import { errorToast, successToast } from 'v2/utils/toasts'
import { getNewFlowData } from './config/base.config'
import { usePlaygroundStreamTransport } from './hooks/use-playground-stream-transport'
import { useFlowRunStore } from './run-info/store'
import { createAsyncMessagePayload } from './run-info/types'
import { suggestErrorText } from './sidebar/ai-agents/collection-configuration-config'
import type { CollectionSuggestPayload } from './sidebar/ai-agents/collection-configuration-types'
import type { ConversationGoal } from './types'
import {
  type FlowData,
  FlowRunStatus,
  type JsonValue,
  type RFlowPayload,
  StartNodeTypes,
} from './types'
import { inverseTransformFlowData } from './utils/data-transformer'
import { isFlowRunLogsExpired } from './utils/flow-run'
import { voiceListLanguage } from './utils/lang-utils'
import { resolvePhloUpsertId, swapBuilderPathId } from './utils/phlo-upsert-id'

export const usePhloMutation = () => {
  const { id } = useParams()
  const navigate = useAppNavigate()
  const location = useLocation()

  return useMutation({
    mutationKey: flowQueryKeys().flowData(id ?? 'unsaved'),
    mutationFn: async (data: {
      payload: RFlowPayload
      nodeMap?: Record<string, string>
      id?: string
      skipQueryInvalidation?: boolean
    }) => {
      // SER-6234: regenerate a fresh UUID if the URL-derived id is invalid, so the frontend
      // never persists a PHLO with a non-UUID id (a hand-edited builder URL would otherwise
      // break pg2ch -> ClickHouse's strict UUID column). onSuccess adopts the resulting id.
      const idToUse = resolvePhloUpsertId(data.id ?? id)
      const resp = await flowService.upsertFlow(data.payload, idToUse)
      return resp.data
    },
    onSuccess: (data, variables) => {
      // Editing an active/paused flow (and publishing) mints a new version, so refresh
      // the version-history list — otherwise the dropdown stays stale until a full
      // reload. Above the skip below on purpose: it's a separate query key, so the
      // uuid-race reason Vibe saves skip flow-config invalidation doesn't apply, and
      // a Vibe save must still surface its new version in an open dropdown. (SER-5591)
      const backendId = data.phlo?.id
      if (backendId) {
        queryClient.invalidateQueries({ queryKey: flowQueryKeys().versions(backendId) })
      }

      // Skip flow-config invalidation and the ?new strip when saving from Vibe Agent: both would
      // re-enable/refetch the flow-config query and clobber in-flight streaming updates whose
      // node ids differ from the just-saved ones (SER-6079/5591). Still adopt a regenerated id
      // into the URL (SER-6234) — keeping ?new so isNew stays true and no refetch fires.
      if (variables?.skipQueryInvalidation) {
        if (backendId && id && backendId !== id) {
          navigate(
            { pathname: swapBuilderPathId(location.pathname, backendId), search: location.search },
            { replace: true },
          )
        }
        return
      }

      if (backendId) {
        queryClient.invalidateQueries({ queryKey: flowQueryKeys().flowData(backendId) })
      }
      queryClient.invalidateQueries({ queryKey: flowQueryKeys().flowsBase })

      // `?new` is set when an agent is created, so we only fire on first save, not edits.
      // Strip it through the router, NOT window.history.replaceState — the router never
      // observes a raw replaceState, so useFlowId().isNew would stay true and pin the
      // 'Unsaved' badge + the id-less blank flow-config cache (disabled Test agent) until a
      // full reload. The isNew flip also re-enables the flow-config query, which then fetches
      // the just-saved flow. Read from router location too, so read and write share one source
      // of truth. (SER-6079)
      const params = new URLSearchParams(location.search)
      const wasNew = params.has('new')
      if (wasNew) {
        track(AnalyticsEvents.AGENT_CREATED, {
          has_prompt: !!variables.payload.global_meta?.system_prompt?.prompt,
        })
        params.delete('new')
        // The onboarding marker's job is done once the agent exists — without
        // this it rides bookmarks and shared links indefinitely.
        params.delete('onb')
      }

      // SER-6234: adopt the (possibly regenerated) backend id into the URL when it differs from
      // the URL id. Route isn't id-keyed → no remount, canvas preserved; the flow-config query
      // re-points and re-hydrates from the saved flow. swapBuilderPathId rewrites the id segment
      // positionally — not by matching the decoded useParams id against the raw, encoded pathname.
      if (backendId && id && backendId !== id) {
        navigate(
          { pathname: swapBuilderPathId(location.pathname, backendId), search: params.toString() },
          { replace: true },
        )
      } else if (wasNew) {
        navigate({ pathname: location.pathname, search: params.toString() }, { replace: true })
      }
    },
  })
}

export interface GenerateFunctionSchemaResponse {
  schema: string
}

// ai-assist is not on the embed allow-list. Throwing takes the onError path a 403 would,
// minus the request (resolving would reach onSuccess with no payload).
const refuseAiAssistInEmbed = () => {
  if (isEmbedMode()) {
    throw new Error('AI assist is not available in embedded mode')
  }
}

// Org catalogues whose services are not on the embed allow-list (ai-assist, plivocx, sentinel).
const canReadDeferredOrgCatalogues = () => !isEmbedMode()

export const useGenerateFunctionSchemaMutation = () =>
  useMutation({
    mutationFn: async (params: GenerateFunctionSchemaParams) => {
      refuseAiAssistInEmbed()
      const response = await flowService.generateFunctionSchema(params)
      return response.data
    },
  })

export const useGenerateHttpFunctionSchemaMutation = () =>
  useMutation({
    mutationFn: async (params: GenerateHttpFunctionSchemaParams) => {
      refuseAiAssistInEmbed()
      const response = await flowService.generateHttpFunctionSchema(params)
      return response.data
    },
  })

// V2.9 Inputs: agent-runner infers each Input's collect type + options from the node text.
export const useSuggestCollectionConfigurationMutation = () =>
  useMutation({
    mutationFn: (payload: CollectionSuggestPayload) =>
      flowService.suggestCollectionConfiguration(payload),
    onError: (err) =>
      errorToast({
        title: 'Could not suggest a collection configuration',
        // the runner names the keys it refused; the bare "Validation failed" names nothing
        description: suggestErrorText(err) ?? getErrorText(err as AxiosError),
      }),
  })

export const useGenerateCodeMutation = () =>
  useMutation({
    mutationFn: async (params: GenerateCodeParams) => {
      refuseAiAssistInEmbed()
      const response = await flowService.generateCode(params)
      return response.data
    },
  })

export const useGenerateIntentNameMutation = () =>
  useMutation({
    mutationFn: async ({
      instruction,
      fallbackName,
      existingIntents,
    }: {
      instruction: string
      fallbackName: string
      existingIntents?: Array<{
        intent_name: string
        intent_instructions: string
      }>
    }) => {
      refuseAiAssistInEmbed()
      const response = await flowService.generateIntentName(instruction, existingIntents)
      return response.data?.intent_name || fallbackName
    },
  })

export const useGenerateGoalsMutation = () => {
  const { id } = useParams()
  return useMutation({
    mutationFn: async (conversationGoals: ConversationGoal[]) => {
      if (!id) throw new Error('Flow ID is required')
      const response = await flowService.generateGoals(id, conversationGoals)
      return response?.data?.goals ?? []
    },
  })
}

export const useGenerateNodeMetadataMutation = () =>
  useMutation({
    mutationKey: flowQueryKeys().generateNodeMetadata,
    mutationFn: async ({
      nodeType,
      config,
      existingNodes,
      nodeId,
      existingName,
      existingDescription,
    }: {
      nodeType: string
      config: object
      existingNodes?: Array<{ name: string; config: object }>
      nodeId?: string
      existingName?: string
      existingDescription?: string
    }) => {
      agentLogger.debug('Generating node metadata for nodeId', nodeId)
      const response = await flowService.generateNodeMetadata({
        nodeType,
        config,
        existingNodes,
        existingName,
        existingDescription,
      })
      return {
        node_name: response.data?.node_name,
        node_description: response.data?.node_description || null,
      }
    },
  })

// When getFlowConfig returns 5xx, the query enters error state. Combined with
// refetchOnMount:'always' and broadcastQueryClient (IDB persistence), this causes
// the component to re-render/remount, which triggers a new fetch, creating an
// infinite refetch loop (~3.5k calls in 30s). Module-level Map tracks failures
// per phloID+version (survives remounts) and disables the query after
// MAX_FLOW_QUERY_FAILURES. Keyed by version too so a failing version-preview fetch
// can't disable the live canvas query (same phloID), nor a successful preview clear
// the live canvas's failure count.
const MAX_FLOW_QUERY_FAILURES = 3
const flowQueryFailures = new Map<string, number>()

export const useFlowQuery = (phloID: string, phloVersion?: string, isNew?: boolean) => {
  const failureKey = `${phloID}:${phloVersion ?? ''}`
  return useQuery({
    queryKey: flowQueryKeys().flowData(phloID, phloVersion),
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
    queryFn: async () => {
      try {
        const resp = await flowService.getFlowConfig(phloID, phloVersion)
        flowQueryFailures.delete(failureKey)
        return inverseTransformFlowData(resp.data)
      } catch (error) {
        // Return null on 404 to prevent infinite refetch loop —
        // error state + refetchOnMount:'always' + IDB persistence causes repeated fetches
        if (axios.isAxiosError(error) && error.response?.status === 404) return null
        const failures = (flowQueryFailures.get(failureKey) ?? 0) + 1
        flowQueryFailures.set(failureKey, failures)
        // Last failure before the breaker disables the query: resolve to null instead of
        // throwing, so the FlowCanvasContent self-heal clears a stuck `?version` preview
        // (viewVersion(null) + toast) rather than leaving the read-only banner pinned over
        // whatever was last hydrated, un-refetchable until a full reload. (SER-5591)
        if (failures >= MAX_FLOW_QUERY_FAILURES) return null
        throw error
      }
    },
    // New flow: no BE row exists yet (a GET would 404), so the query stays disabled and
    // the blank canvas is seeded through initialData instead of a queryFn short-circuit.
    // initialDataUpdatedAt: 0 marks the seed stale so the isNew→false flip after the
    // first save (router navigate in usePhloMutation.onSuccess) enables the query AND
    // immediately fetches the saved flow — a queryFn returning the blank would instead
    // rewrite the cache with an id-less flow on every post-save refetch. (SER-6079)
    initialData: isNew ? () => inverseTransformFlowData(getNewFlowData()) : undefined,
    initialDataUpdatedAt: isNew ? 0 : undefined,
    staleTime: convertToMs(1, TimeUnits.Minutes),
    refetchOnMount: 'always',
    enabled:
      !!phloID && !isNew && (flowQueryFailures.get(failureKey) ?? 0) < MAX_FLOW_QUERY_FAILURES,
  })
}

// change_summary is filled asynchronously a few seconds after a save (SER-5645), so
// poll while a RECENTLY minted version is still awaiting it. The recency window means
// permanently-null versions (pre-feature, or a generation that failed) never trigger
// polling, and a pending summary that never lands ages out on its own — so this
// self-caps without an explicit attempt counter. The query is already gated on the
// dropdown/banner being open, so polling only runs while the history is visible.
// The window is a UX floor, not a correctness guarantee: a summary that lands after it
// (or under a >120s client clock skew) still shows on the next open, since the query
// refetches on mount (staleTime 0).
const SUMMARY_PENDING_WINDOW_SECONDS = 120
// `enabled` gates fetching (dropdown closed, not viewing historical) while the query
// key stays the REAL flow id — an '' sentinel key would be one cache entry silently
// shared across every flow that ever passed a blank id.
export const useVersionsQuery = (phloID: string | undefined, enabled = true) =>
  useQuery({
    queryKey: flowQueryKeys().versions(phloID ?? ''),
    queryFn: async () => {
      const resp = await flowService.listVersions(phloID ?? '')
      return resp.data
    },
    enabled: !!phloID && enabled,
    refetchInterval: (query) => {
      const nowSeconds = Date.now() / 1000
      const pending = query.state.data?.versions?.some(
        (v) =>
          v.change_summary == null && nowSeconds - v.created_at < SUMMARY_PENDING_WINDOW_SECONDS,
      )
      return pending ? 4000 : false
    },
  })

export const useSwitchVersion = () =>
  useMutation({
    mutationFn: async ({
      phloID,
      sourceVersionId,
    }: {
      phloID: string
      sourceVersionId: number
    }) => {
      const resp = await flowService.switchVersion(phloID, sourceVersionId)
      return resp.data
    },
    onSuccess: (_data, { phloID }) => {
      queryClient.invalidateQueries({ queryKey: flowQueryKeys().flowData(phloID) })
      queryClient.invalidateQueries({ queryKey: flowQueryKeys().versions(phloID) })
      queryClient.invalidateQueries({ queryKey: flowQueryKeys().flowsBase })
      successToast({
        title: 'Version switched',
        description: 'New conversations will use this version.',
      })
    },
    onError: (err) => {
      errorToast({ title: 'Error', description: getErrorText(err as AxiosError) })
    },
  })

export const useEditVersionSummary = () =>
  useMutation({
    mutationFn: async ({
      phloID,
      versionId,
      summary,
      description,
    }: {
      phloID: string
      versionId: number
      summary: string
      // undefined = user never touched the field -> the key is omitted from the
      // request and the stored description stays as-is (title-only edit).
      description?: string
    }) => {
      const resp = await flowService.editVersionSummary(phloID, versionId, summary, description)
      return resp.data
    },
    onSuccess: (_data, { phloID }) => {
      queryClient.invalidateQueries({ queryKey: flowQueryKeys().versions(phloID) })
      successToast({ title: 'Description updated' })
    },
    onError: (err) => {
      errorToast({ title: 'Error', description: getErrorText(err as AxiosError) })
    },
  })

export const useBusinessHoursQuery = () =>
  useInfiniteQuery({
    queryKey: flowQueryKeys().businessHoursForFlow,
    queryFn: async ({ pageParam = 1 }) => {
      const resp = await settingsService.getBusinessHours({ page: `${pageParam}`, limit: '100' })
      return resp.data.business_hours
    },
    getNextPageParam: (lastPage, allPages) =>
      lastPage.length === 100 ? allPages.length + 1 : undefined,
    initialPageParam: 1,
    // Org-wide; hodor refuses it to sub-account embed sessions.
    enabled: !isSubAccountEmbedSession(),
  })

export const useFlowStateMutation = () =>
  useMutation({
    mutationFn: async ({ id, action }: { id: string; action: 'pause' | 'resume' }) => {
      if (action === 'pause') {
        return settingsService.pauseFlow(id)
      }
      return settingsService.resumeFlow(id)
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: flowQueryKeys().flowData(id) })
      queryClient.invalidateQueries({ queryKey: flowQueryKeys().flowsBase })
    },
  })

export const useAppVariablesQuery = () =>
  useQuery({
    queryKey: flowQueryKeys().appVariables,
    enabled: canReadDeferredOrgCatalogues(),
    queryFn: async () => {
      const resp = await externalService.getAppVariables()
      return resp.data
    },
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: 'always',
  })

export const useMapActionParametersMutation = () =>
  useMutation({
    mutationFn: async (params: RMapActionParametersPayload) => {
      refuseAiAssistInEmbed()
      const resp = await flowService.mapActionParameters(params)
      return resp.data
    },
  })

// currentVoiceId is the flow's already-saved voice. Sentinel always returns it, even
// when the language filter would otherwise drop it, so an existing flow keeps seeing
// its saved voice. It is part of the key so caches for different saved voices never collide.
export const useVoicesQuery = (selectedLanguage: string, currentVoiceId?: string) => {
  const language = voiceListLanguage(selectedLanguage)
  return useQuery({
    queryKey: flowQueryKeys().voices(language, currentVoiceId),
    queryFn: async () => {
      const resp = await flowService.getVoices(language, currentVoiceId)
      return resp.data
    },
    staleTime: convertToMs(5, TimeUnits.Minutes),
    gcTime: convertToMs(10, TimeUnits.Minutes),
    enabled: !!language && canReadDeferredOrgCatalogues(),
  })
}

export const useTextToSpeechMutation = () =>
  useMutation({
    mutationFn: async (params: {
      text: string
      voice_id: string
      model_id?: string
      provider_id?: string
      language_code?: string
    }) => {
      refuseAiAssistInEmbed()
      const resp = await flowService.textToSpeech(params)
      return resp.data
    },
  })

export const useSecretVariableMutation = () =>
  useMutation({
    mutationFn: async (payload: {
      id?: string
      name: string
      value: string
      description: string
    }) => {
      if (payload.id) {
        const { id, ...rest } = payload
        return flowService.updateSecretVariable(id, rest)
      }
      return flowService.createSecretVariable(payload)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: flowQueryKeys().secretVariables })
    },
    onError: (error, payload) => {
      errorToast({
        title: `Error ${payload.id ? 'updating' : 'creating'} secret variable`,
        description: getErrorText(error as AxiosError),
      })
    },
  })

export const useDeleteSecretVariableMutation = () =>
  useMutation({
    mutationFn: async (id: string) => flowService.deleteSecretVariable(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: flowQueryKeys().secretVariables })
      successToast({
        title: 'Secret variable deleted',
        description: 'The secret variable has been deleted',
      })
    },
  })

export const useSecretVariablesQuery = () =>
  useQuery({
    queryKey: flowQueryKeys().secretVariables,
    queryFn: async () => {
      const resp = await flowService.listSecretVariables()
      return resp.data
    },
    enabled: canReadDeferredOrgCatalogues(),
  })

export const useTriggerApiRequestMutation = () =>
  useMutation({
    mutationFn: async (params: {
      flowId: string
      authId: string
      queryParams?: Record<string, string | number | boolean>
      body?: Record<string, JsonValue>
      isLegacy?: boolean
    }) => {
      const resp = await flowService.triggerApiRequest(
        params.authId,
        params.flowId,
        params.queryParams,
        params.body,
        params.isLegacy,
      )
      return resp.data
    },
    onError: (error) => {
      useFlowRunStore.getState().reset()
      errorToast({
        title: 'Error triggering API request',
        description: getErrorText(error as AxiosError),
      })
    },
  })

// Generic hook for async messaging (WhatsApp, SMS, etc.)
export const useTriggerAsyncMessageMutation = () => {
  const playgroundStreamTransportEnabled = usePlaygroundStreamTransport()
  return useMutation({
    mutationFn: async (payload: {
      authId: string
      messageUUID: string
      from: string
      to: string
      body: string
      phloUUID: string
      channelType: 'whatsapp' | 'message'
      isLegacy?: boolean
    }) => {
      const { channelType, ...messagePayload } = payload

      // SER-6086: the seed endpoints are unnecessary on the stream transport —
      // the first message rides the same POST as every other one (the backend
      // start-vs-continues on redis state). Legacy phlos keep their own path.
      if (playgroundStreamTransportEnabled && !messagePayload.isLegacy) {
        return playgroundService.sendMessage(
          createAsyncMessagePayload(
            {
              authId: messagePayload.authId,
              messageUUID: messagePayload.messageUUID,
              from: messagePayload.from,
              to: messagePayload.to,
              body: messagePayload.body,
              text: messagePayload.body,
              phloId: messagePayload.phloUUID,
              aomUUID: getLocalAuth().aom_uuid,
            },
            channelType === 'whatsapp' ? ConvChannels.WHATSAPP : ConvChannels.MESSAGE,
          ),
        )
      }

      if (channelType === 'whatsapp') {
        const response = await executionService.triggerWhatsAppIncomingMessage({
          AuthID: messagePayload.authId,
          MessageUUID: messagePayload.messageUUID,
          From: messagePayload.from,
          To: messagePayload.to,
          Body: messagePayload.body,
          Type: 'whatsapp',
          ContentType: 'text',
          PhloUUID: messagePayload.phloUUID,
        })
        return response.data
      }
      if (messagePayload.isLegacy) {
        // Legacy (migrated) phlos run on cx-flow-runner — start directly via
        // Hodor's /v2 proxy; queue-execution-service is not involved. The body
        // mirrors a real Plivo message_url POST (CamelCase fields), so the
        // playground exercises the exact production wire shape.
        const response = await flowService.triggerStartFlowRunner(
          messagePayload.authId,
          messagePayload.phloUUID,
          { ispg: true },
          {
            MessageUUID: messagePayload.messageUUID,
            From: messagePayload.from,
            To: messagePayload.to,
            Type: 'sms',
            Text: messagePayload.body,
          },
        )
        return response.data
      }
      const response = await executionService.triggerMessageIncomingMessage({
        AuthID: messagePayload.authId,
        MessageUUID: messagePayload.messageUUID,
        From: messagePayload.from,
        To: messagePayload.to,
        Text: messagePayload.body,
        Type: 'sms',
        ContentType: 'text',
        PhloUUID: messagePayload.phloUUID,
      })
      return response.data
    },
    onError: (error) => {
      useFlowRunStore.getState().setIsFlowRunning(false)
      errorToast({
        title: 'Error triggering async message',
        description: getErrorText(error as AxiosError),
      })
    },
  })
}

export const useInitializePlaygroundMutation = () => {
  const { mutate: triggerApiRequest } = useTriggerApiRequestMutation()
  const { mutate: triggerAsyncMessage } = useTriggerAsyncMessageMutation()
  const { reset, setIsFlowRunning, setAsyncAgentMeta } = useFlowRunStore()
  // const { subscribeMutation } = usePubnubMutations()
  return useMutation({
    mutationFn: async (flowData: FlowData) => {
      if (!flowData.phlo.id) {
        throw new Error('Playground initialization failed: Agent Id is missing.')
      }

      const message = 'Hi, hello'
      try {
        const resp = await flowService.initializePlayground(flowData.phlo.id, { message })
        const {
          flow_type: flowType,
          sip_uri: sipUri,
          flow_uuid: flowId,
          auth_id: authId,
          chat_data: chatData,
        } = resp.data

        reset()
        setIsFlowRunning(true)
        switch (flowType) {
          case FlowTypes.incoming_call:
          case FlowTypes.outgoing_call:
            if (!sipUri) {
              throw new Error('Playground initialization failed: SIP URI is missing.')
            }
            await phoneSDK.makeCall({
              callWith: sipUri,
              callerId: {
                name: 'Playground',
              },
              contactName: 'Playground',
              flowType: FlowSourceTypes[flowType as FlowTypes],
              aomUUID: getLocalAuth().aom_uuid,
            })
            break
          case FlowTypes.api_request:
            triggerApiRequest(
              {
                flowId,
                authId,
                queryParams: {
                  ispg: true,
                },
                isLegacy: flowData.phlo.is_legacy,
              },
              {
                onSettled: () => {
                  setIsFlowRunning(false)
                },
              },
            )
            break
          case FlowTypes.incoming_whatsapp: {
            const { from, to } = { from: v4(), to: v4() }
            setAsyncAgentMeta({ from, to, channelName: ConvChannels.WHATSAPP })
            setIsFlowRunning(true)

            triggerAsyncMessage({
              authId,
              messageUUID: v4(),
              from,
              to,
              body: 'hello',
              phloUUID: flowData.phlo.id,
              channelType: 'whatsapp',
            })
            break
          }
          case FlowTypes.incoming_sms: {
            const { from, to } = { from: v4(), to: v4() }
            setAsyncAgentMeta({ from, to, channelName: ConvChannels.MESSAGE })
            setIsFlowRunning(true)

            triggerAsyncMessage({
              authId,
              messageUUID: v4(),
              from,
              to,
              body: 'hello',
              phloUUID: flowData.phlo.id,
              channelType: 'message',
              isLegacy: flowData.phlo.is_legacy,
            })
            break
          }
          case FlowTypes.incoming_chat: {
            phoneLogger.info('Playground: Incoming chat flow type initialized: ', chatData)
            if (!chatData) {
              throw new Error('Playground initialization failed: Chat app data is missing.')
            }
            const { from, to } = { from: v4(), to: v4() }
            setAsyncAgentMeta({
              from,
              to,
              channelName: ConvChannels.CHAT,
              chatChannel: chatData.channelName,
            })
            // subscribeMutation.mutate({ channels: [chatData.channelName] })
            setIsFlowRunning(true)
            break
          }
          default:
            phoneLogger.error('Playground: Unsupported flow type:', flowType)
            setIsFlowRunning(false)
            break
        }
      } catch (error) {
        phoneLogger.error('Playground: Error during flow initialization:', error)
        errorToast({
          title: 'Playground initialization failed',
          description: getErrorText(error as AxiosError),
        })
        setIsFlowRunning(false)
      }
    },
  })
}
export const useGetSlackChannelsQuery = (botToken: string | undefined) => {
  const debouncedBotToken = useDebounce(botToken, 1000)
  return useQuery({
    queryKey: flowQueryKeys().slackChannels(debouncedBotToken),
    queryFn: async () => {
      if (!debouncedBotToken?.startsWith('xoxb-')) return { channels: [] }
      const resp = await flowService.getSlackChannels({ bot_token: debouncedBotToken })
      return resp.data
    },
    enabled: !isEmbedMode() && !!debouncedBotToken?.startsWith('xoxb-'),
  })
}

export const useGetSlackCreateAppLinkQuery = (type: string | undefined) =>
  useQuery({
    queryKey: flowQueryKeys().slackCreateAppLink,
    queryFn: async () => {
      const resp = await flowService.getSlackCreateAppLink()
      return resp.data
    },
    // Slack routes are not on the embed allow-list.
    enabled: type === StartNodeTypes.incoming_slack && !isEmbedMode(),
  })

export const useGetFlowRunDetailsQuery = (runId: string) => {
  const { aoConversationQualityEnabled } = useFeatureFlags()
  const evalSource = aoConversationQualityEnabled ? 'ao' : undefined
  const flowRunDetailsQuery = useQuery({
    queryKey: flowQueryKeys().flowRunDetails(runId || '', evalSource),
    queryFn: () => flowService.getFlowRunDetails(runId || '', evalSource),
    enabled: !!runId,
    retry: 1,
    refetchInterval: (query) => {
      const data = query.state.data?.data
      const startedAt = data?.run?.started_at
      const endedAt = data?.run?.ended_at
      const status = data?.run?.status
      const hasLogs = !!data?.logs?.some((log) => log.event_name === 'NODE_EXECUTED')

      // If started_at is less than 3 minutes from current time, refetch every 5 seconds
      // This is to fetch logs for completed flow runs as they are not available in the backend immediately
      if (startedAt && dayjs().diff(dayjs(startedAt * 1000), 'minute') < 3) {
        return 5000
      }

      // If logs are present and status is not IN_PROGRESS, stop refetching
      if (hasLogs && status !== FlowRunStatus.IN_PROGRESS) {
        return false
      }

      // If no logs and status is not IN_PROGRESS, refetch every 2 seconds
      // If the flow run logs are not expired, refetch every 2 seconds
      if (!hasLogs && !isFlowRunLogsExpired(endedAt)) {
        return 2000
      }

      // If status is IN_PROGRESS, refetch every 10 seconds
      if (status === FlowRunStatus.IN_PROGRESS) {
        return 10000
      }

      // Default: stop refetching
      return false
    },
  })

  return flowRunDetailsQuery
}
